// FILE: client/src/pages/secure/EscrowManagementPage.js
// (This file is updated for performance and to fix rendering loops)

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import DataList from '../../components/shared/DataList';

// --- Add/Edit Account Modal ---
const AccountModal = ({ isOpen, onClose, onSave, account }) => {
    const [formData, setFormData] = useState({ bank_name: '', account_name: '', account_number: '', authorized_signers: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [saveError, setSaveError] = useState('');
    const apiClient = useApiClient();
    const isEditMode = !!account;

    useEffect(() => {
        if (isOpen) {
            if (isEditMode) {
                setFormData({ ...account });
            } else {
                setFormData({ bank_name: '', account_name: '', account_number: '', authorized_signers: '' });
            }
        }
    }, [isOpen, account, isEditMode]);

    const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSaveError('');
        try {
            const savedAccount = isEditMode
                ? await apiClient.put(`/api/escrow/accounts/${account.account_id}`, formData)
                : await apiClient.post('/api/escrow/accounts', formData);
            onSave(savedAccount, isEditMode);
            onClose();
        } catch (error) {
            console.error("Failed to save account", error);
            setSaveError(error.message || 'Could not save the account.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
            <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-lg">
                <h2 className="text-2xl font-bold text-slate-800 mb-6">{isEditMode ? 'Edit' : 'Add New'} Escrow Account</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <input name="bank_name" value={formData.bank_name} onChange={handleChange} placeholder="Bank Name" required className="p-2 border rounded w-full" />
                    <input name="account_name" value={formData.account_name} onChange={handleChange} placeholder="Account Name" required className="p-2 border rounded w-full" />
                    <input name="account_number" value={formData.account_number} onChange={handleChange} placeholder="Account Number" required className="p-2 border rounded w-full" />
                    <input name="authorized_signers" value={formData.authorized_signers} onChange={handleChange} placeholder="Authorized Signers" className="p-2 border rounded w-full" />
                    <div className="grid grid-cols-2 gap-3">
                        <input name="depository_state" value={formData.depository_state || ''} onChange={(e) => setFormData(prev => ({ ...prev, depository_state: e.target.value.toUpperCase().slice(0, 2) }))} placeholder="Depository state (e.g. IL, FL)" className="p-2 border rounded w-full" />
                        <select name="depository_kind" value={formData.depository_kind || ''} onChange={handleChange} className="p-2 border rounded w-full">
                            <option value="">Type of depository</option><option value="bank">Bank</option><option value="credit_union">Credit union</option>
                            <option value="savings_association">Savings association</option><option value="trust_company">Trust company</option><option value="title_company">Title company</option>
                        </select>
                    </div>
                    {saveError && <p className="text-sm text-red-600">{saveError}</p>}
                    <div className="flex justify-end gap-4 pt-4">
                        <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
                        <button type="submit" disabled={isSubmitting} className="px-4 py-2 bg-slate-800 text-white rounded disabled:bg-indigo-400">
                            {isSubmitting ? 'Saving...' : 'Save Account'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};


// --- Main Page Component ---
const EscrowManagementPage = () => {
    const [accounts, setAccounts] = useState([]);
    const [journalEntries, setJournalEntries] = useState([]);
    const [transactionLedgers, setTransactionLedgers] = useState([]);
    
    // State for pagination to improve performance
    const [journalPage, setJournalPage] = useState(1);
    const [hasMoreJournal, setHasMoreJournal] = useState(true);
    
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [editingAccount, setEditingAccount] = useState(null);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [searchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState(searchParams.get('tab') === 'ledgers' ? 'ledgers' : 'journal');
    const [ledgerFilter, setLedgerFilter] = useState('open');
    const apiClient = useApiClient();

    // Optimized data fetching to run only once on mount
    const fetchInitialData = useCallback(async () => {
        setIsLoading(true);
        try {
            const [accountsData, journalData, ledgersData] = await Promise.all([
                apiClient.get('/api/escrow/accounts'),
                apiClient.get(`/api/escrow/journal?page=1`), // Fetch only the first page initially
                apiClient.get('/api/escrow/ledgers')
            ]);
            setAccounts(accountsData);
            setJournalEntries(journalData.entries);
            setHasMoreJournal(journalData.hasMore);
            setTransactionLedgers(ledgersData);
            setError(null);
        } catch (err) {
            setError(err.message || 'Failed to fetch escrow data.');
        } finally {
            setIsLoading(false);
        }
    }, [apiClient]);

    useEffect(() => {
        fetchInitialData();
    }, [fetchInitialData]);

    // Function to load more journal entries when the button is clicked
    const fetchMoreJournalEntries = async () => {
        if (isLoadingMore || !hasMoreJournal) return;
        
        setIsLoadingMore(true);
        const nextPage = journalPage + 1;
        try {
            const data = await apiClient.get(`/api/escrow/journal?page=${nextPage}`);
            setJournalEntries(prev => [...prev, ...data.entries]);
            setHasMoreJournal(data.hasMore);
            setJournalPage(nextPage);
        } catch (err) {
            console.error("Failed to fetch more journal entries", err);
        } finally {
            setIsLoadingMore(false);
        }
    };

    const handleSaveAccount = (savedAccount, isEditMode) => {
        if (isEditMode) {
            setAccounts(prev => prev.map(acc => acc.account_id === savedAccount.account_id ? savedAccount : acc));
        } else {
            setAccounts(prev => [...prev, savedAccount]);
        }
    };

    const handleDeleteAccount = async (accountId) => {
        if (window.confirm('Are you sure you want to delete this account? This action is permanent and cannot be undone.')) {
            try {
                await apiClient.delete(`/api/escrow/accounts/${accountId}`);
                setAccounts(prev => prev.filter(acc => acc.account_id !== accountId));
            } catch (error) {
                alert(error.response?.data?.message || 'Failed to delete account.');
            }
        }
    };

    const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

    return (
        <div className="p-4 md:p-8">
            <AccountModal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} onSave={handleSaveAccount} />
            <AccountModal isOpen={!!editingAccount} onClose={() => setEditingAccount(null)} onSave={handleSaveAccount} account={editingAccount} />

            <div className="space-y-8">
                {error && <div className="p-4 text-red-700 bg-red-100 rounded-lg">{error}</div>}

                {/* Master Escrow Account Log */}
                <div className="bg-white p-6 md:p-8 rounded-lg shadow-lg">
                    <div className="flex justify-between items-center mb-6">
                        <h1 className="text-2xl font-bold text-slate-800">Master Escrow Account Log</h1>
                        <button onClick={() => setIsAddModalOpen(true)} className="px-4 py-2 text-sm text-white bg-slate-800 rounded-md hover:bg-slate-900">Add New Account</button>
                    </div>
                    {isLoading ? <p>Loading accounts...</p> : (
                        <div>
                            <DataList rows={accounts} rowKey="account_id" csvName="escrow-accounts" defaultSort={{ key: 'bank_name', dir: 'asc' }}
                                empty="No escrow accounts yet. Add the first one."
                                columns={[
                                    { key: 'bank_name', label: 'Bank Name' },
                                    { key: 'account_name', label: 'Account Name' },
                                    { key: 'account_number', label: 'Account Number' },
                                    { key: 'actions', label: 'Actions', sortable: false, csv: false, search: false, render: (acc) => (
                                        <span className="space-x-4 text-sm font-medium whitespace-nowrap">
                                            <Link to={`/dashboard/escrow/${acc.account_id}`} className="text-indigo-600 hover:underline">Manage Journal</Link>
                                            <button onClick={() => setEditingAccount(acc)} className="text-blue-600 hover:underline">Edit</button>
                                            <button onClick={() => handleDeleteAccount(acc.account_id)} className="text-red-600 hover:underline">Delete</button>
                                        </span>) },
                                ]} />
                        </div>
                    )}
                </div>

                {/* Tabbed view for Journal and Ledgers */}
                <div className="bg-white p-6 md:p-8 rounded-lg shadow-lg">
                    <div className="border-b border-gray-200">
                        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                            <button onClick={() => setActiveTab('journal')} className={`${activeTab === 'journal' ? 'border-slate-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}>
                                Master Journal (All Accounts)
                            </button>
                            <button onClick={() => setActiveTab('ledgers')} className={`${activeTab === 'ledgers' ? 'border-slate-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}>
                                Ledgers
                            </button>
                        </nav>
                    </div>

                    {/* Journal Tab Content */}
                    {activeTab === 'journal' && (
                        <div className="mt-6">
                            <h2 className="text-2xl font-bold text-slate-800 mb-6">Master Journal</h2>
                            {isLoading ? <p>Loading journal entries...</p> : (
                                <div>
                                    <DataList rows={journalEntries} rowKey="entry_id" csvName="escrow-master-journal" defaultSort={{ key: 'entry_date', dir: 'desc' }}
                                        empty="No journal entries yet." filters={[{ key: 'entry_type', label: 'Type' }, { key: 'account_name', label: 'Account' }]}
                                        note={hasMoreJournal ? `Searching the ${journalEntries.length} entries loaded — use Load More for older ones.` : undefined}
                                        columns={[
                                            { key: 'entry_date', label: 'Date', type: 'date' },
                                            { key: 'account_name', label: 'Account' },
                                            { key: 'street_address', label: 'Transaction' },
                                            { key: 'payee_or_payor', label: 'Payor/Payee' },
                                            { key: 'entry_type', label: 'Type', render: (e) => <span className={e.entry_type === 'Deposit' ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>{e.entry_type}</span> },
                                            { key: 'amount', label: 'Amount', type: 'number', render: (e) => formatCurrency(e.amount) },
                                        ]} />
                                    {hasMoreJournal && (
                                        <div className="mt-6 text-center">
                                            <button onClick={fetchMoreJournalEntries} disabled={isLoadingMore} className="px-4 py-2 text-sm text-white bg-slate-800 rounded-md hover:bg-slate-900 disabled:bg-indigo-400">
                                                {isLoadingMore ? 'Loading...' : 'Load More'}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Ledger Tab Content — one ledger per transaction, lease or owner, 1450.750(i)(2) */}
                    {activeTab === 'ledgers' && (
                        <div className="mt-6">
                            <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
                                <h2 className="text-2xl font-bold text-slate-800">Ledgers</h2>
                                <div className="flex gap-1 text-sm">
                                    {['open', 'closed', 'all'].map(f => (
                                        <button key={f} onClick={() => setLedgerFilter(f)}
                                            className={`px-3 py-1 rounded ${ledgerFilter === f ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}>
                                            {f[0].toUpperCase() + f.slice(1)}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <p className="text-sm text-slate-500 mb-4">One ledger for each transaction, naming all parties. Every journal entry is filed on one.</p>
                            {isLoading ? <p>Loading ledgers...</p> : (
                                <div>
                                    <DataList rows={transactionLedgers.filter(l => ledgerFilter === 'all' || l.status === ledgerFilter)} rowKey="ledger_id" csvName={`escrow-ledgers-${ledgerFilter}`}
                                        defaultSort={{ key: 'last_activity', dir: 'desc' }} empty={`No ${ledgerFilter === 'all' ? '' : ledgerFilter} ledgers.`}
                                        filters={[{ key: 'funds_type', label: 'Funds' }, { key: 'account_name', label: 'Account' }]}
                                        columns={[
                                            { key: 'description', label: 'Ledger', render: (ledger) => (<>
                                                <div className="font-medium text-slate-800">{ledger.description}</div>
                                                <div className="text-xs text-slate-500">#{ledger.ledger_id}{ledger.reference ? ` · ${ledger.reference}` : ''} · {String(ledger.funds_type).replace('_', ' ')}{ledger.status === 'closed' ? ' · closed' : ''}</div>
                                            </>) },
                                            { key: 'funds_type', label: 'Funds', value: (l) => String(l.funds_type || '').replace('_', ' ') },
                                            { key: 'parties', label: 'Parties', render: (l) => l.parties || <span className="text-amber-700">none recorded</span> },
                                            { key: 'account_name', label: 'Account' },
                                            { key: 'last_activity', label: 'Last activity', type: 'date', render: (l) => l.last_activity || '—' },
                                            { key: 'entry_count', label: 'Entries', type: 'number' },
                                            { key: 'balance', label: 'Balance', type: 'number', render: (l) => <span className="font-semibold">{formatCurrency(l.balance)}</span> },
                                            { key: 'view', label: '', sortable: false, csv: false, search: false, render: (l) => <Link to={`/dashboard/escrow/ledgers/${l.ledger_id}`} className="text-indigo-600 hover:underline text-sm">View</Link> },
                                        ]} />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EscrowManagementPage;
